import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api';

const SEV_COLOR  = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#d97706', LOW: '#2563eb', INFO: '#6b7280' };
const SEV_BG     = { CRITICAL: '#fef2f2', HIGH: '#fff7ed', MEDIUM: '#fffbeb', LOW: '#eff6ff', INFO: '#f9fafb' };
const FW_COLOR   = { SOC2: '#7c3aed', ISO27001: '#0369a1', NIST: '#065f46', GDPR: '#9a3412', PCI: '#1e40af' };
const FW_BG      = { SOC2: '#f5f3ff', ISO27001: '#e0f2fe', NIST: '#ecfdf5', GDPR: '#fff7ed', PCI: '#eff6ff' };

export default function ComplianceImpactTab({ orgId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = orgId ? { orgId } : {};
    axios.get('/iasm/compliance-impact', { params })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
      <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />{t('ComplianceImpactTab.loadingComplianceImpact')}
    </div>
  );

  if (!data) return null;

  const { frameworks, totalImpactedFrameworks } = data;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '14px', color: '#374151' }}>
            {t('ComplianceImpactTab.subtitle')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#6b7280' }}>
          <span><strong style={{ color: '#111827' }}>{totalImpactedFrameworks}</strong> {t('ComplianceImpactTab.frameworksImpacted')}</span>
          <span><strong style={{ color: '#111827' }}>{frameworks.reduce((s, f) => s + f.affectedControls, 0)}</strong> {t('ComplianceImpactTab.controlsAtRisk')}</span>
        </div>
      </div>

      {/* Framework cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {frameworks.map(fw => (
          <FrameworkCard
            key={fw.frameworkPrefix}
            fw={fw}
            selected={expanded === fw.frameworkPrefix}
            onClick={() => setExpanded(expanded === fw.frameworkPrefix ? null : fw.frameworkPrefix)}
          />
        ))}
      </div>

      {/* Detail panel for selected framework */}
      {expanded && (() => {
        const fw = frameworks.find(f => f.frameworkPrefix === expanded);
        if (!fw) return null;
        return (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', background: FW_BG[fw.frameworkPrefix] || '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '16px', fontWeight: 700, color: FW_COLOR[fw.frameworkPrefix] || '#374151' }}>{fw.displayName}</span>
                {fw.enrolled && <span style={{ fontSize: '11px', background: FW_COLOR[fw.frameworkPrefix] || '#374151', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{t('ComplianceImpactTab.enrolled')}</span>}
              </div>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                {t('ComplianceImpactTab.controlsAtRiskCount', { count: fw.affectedControls })} · {t('ComplianceImpactTab.findingsCount', { count: fw.uniqueFindings })}
              </span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={thStyle}>{t('ComplianceImpactTab.colControlRef')}</th>
                  <th style={thStyle}>{t('ComplianceImpactTab.colFindingCount')}</th>
                  <th style={thStyle}>{t('ComplianceImpactTab.colHighestSeverity')}</th>
                  <th style={thStyle}>{t('ComplianceImpactTab.colFindingTypes')}</th>
                </tr>
              </thead>
              <tbody>
                {fw.controls.map(ctrl => (
                  <tr key={ctrl.controlRef} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 700, color: FW_COLOR[fw.frameworkPrefix] || '#374151', fontFamily: 'monospace', fontSize: '13px' }}>
                      {fw.frameworkPrefix}:{ctrl.controlRef}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontWeight: 700, fontSize: '15px', color: ctrl.findings > 0 ? '#dc2626' : '#6b7280' }}>{ctrl.findings}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <SeverityPill severity={ctrl.highestSeverity} />
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {ctrl.findingTypes.map(t => (
                          <span key={t} style={{ fontSize: '11px', padding: '2px 7px', background: '#f3f4f6', color: '#374151', borderRadius: '4px' }}>
                            {t.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
                {fw.controls.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                      {t('ComplianceImpactTab.noFindingsControls')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>
  );
}

function FrameworkCard({ fw, selected, onClick }) {
  const { t } = useTranslation();
  const color = FW_COLOR[fw.frameworkPrefix] || '#374151';
  const bg    = FW_BG[fw.frameworkPrefix]    || '#f9fafb';
  const pct   = fw.totalOrgControls > 0
    ? Math.round((fw.affectedControls / fw.totalOrgControls) * 100)
    : null;

  return (
    <div onClick={onClick} style={{
      background: '#fff', border: `2px solid ${selected ? color : '#e5e7eb'}`,
      borderRadius: '10px', padding: '18px', cursor: 'pointer',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      boxShadow: selected ? `0 0 0 3px ${color}22` : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color }}>{fw.displayName}</div>
          {fw.enrolled
            ? <span style={{ fontSize: '10px', fontWeight: 700, color, background: bg, padding: '1px 6px', borderRadius: '3px', marginTop: '3px', display: 'inline-block' }}>{t('ComplianceImpactTab.enrolled')}</span>
            : <span style={{ fontSize: '10px', color: '#9ca3af', marginTop: '3px', display: 'inline-block' }}>{t('ComplianceImpactTab.notEnrolled')}</span>}
        </div>
        {fw.highestSeverity && <SeverityPill severity={fw.highestSeverity} />}
      </div>

      <div style={{ display: 'flex', gap: '20px', marginBottom: '12px' }}>
        <Metric label={t('ComplianceImpactTab.metricFindings')} value={fw.uniqueFindings} color={fw.uniqueFindings > 0 ? '#dc2626' : '#6b7280'} />
        <Metric label={t('ComplianceImpactTab.metricControlsAtRisk')} value={fw.affectedControls} color={fw.affectedControls > 0 ? color : '#6b7280'} />
        {pct !== null && <Metric label={t('ComplianceImpactTab.metricPctAffected')} value={`${pct}%`} color={pct > 20 ? '#dc2626' : pct > 5 ? '#d97706' : '#10b981'} />}
      </div>

      {pct !== null && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ height: '6px', background: '#f3f4f6', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: pct > 20 ? '#dc2626' : pct > 5 ? '#d97706' : '#10b981', borderRadius: '3px', transition: 'width 0.4s' }} />
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>
            {t('ComplianceImpactTab.controlsHaveFindings', { affected: fw.affectedControls, total: fw.totalOrgControls })}
          </div>
        </div>
      )}

      {Object.keys(fw.bySeverity || {}).length > 0 && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].filter(s => fw.bySeverity[s] > 0).map(s => (
            <span key={s} style={{ fontSize: '11px', padding: '2px 7px', background: SEV_BG[s], color: SEV_COLOR[s], borderRadius: '4px', fontWeight: 600 }}>
              {fw.bySeverity[s]} {s}
            </span>
          ))}
        </div>
      )}

      {fw.uniqueFindings === 0 && (
        <div style={{ fontSize: '12px', color: '#10b981' }}>
          <i className="fas fa-check-circle" style={{ marginRight: '4px' }} />{t('ComplianceImpactTab.noFindingsFramework')}
        </div>
      )}

      <div style={{ marginTop: '10px', fontSize: '11px', color: color, fontWeight: 500 }}>
        {selected ? t('ComplianceImpactTab.hideDetails') : t('ComplianceImpactTab.viewAffectedControls')}
      </div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: '20px', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '11px', color: '#9ca3af' }}>{label}</div>
    </div>
  );
}

function SeverityPill({ severity }) {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700,
      background: SEV_BG[severity] || '#f3f4f6',
      color: SEV_COLOR[severity] || '#374151',
    }}>{severity}</span>
  );
}

const thStyle = { padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' };
